// Tier 4 (ALGO TRADING — MT5 & Expert Advisors) — UI components.
// Electric-blue themed (distinct from Tier 1+2 gold and Tier 3 emerald).
// Gates behind Tier 3 "AI TRADER" graduation.
// Milestone gate = 2 auto-verified + 5 self-reported items (30-day autonomous EA).

import React, { useState } from "react";
import { THEME, flexxState } from "./state";
import Portal from "./Portal";
import { FlexxCertificate, openCertificatePrintView } from "./FlexxCertificate";

export const TIER4_PASS_THRESHOLD = 0.75;
export const TIER4_LESSON_XP = 65;        // first-pass XP per Tier 4 lesson
export const TIER4_QUIZ_PASS_BONUS = 10;  // +10 XP per Tier 4 quiz pass
export const TIER4_MODULE_BONUS = 40;     // +40 XP per Tier 4 module complete
export const TIER4_TIER_BONUS = 275;      // Tier 4 completion bonus
export const TIER4_PHASES = ["4.1", "4.2", "4.3", "4.4", "4.5"];
export const TIER4_BLUE = "#3b82f6";
export const TIER4_BLUE_GLOW = "rgba(59,130,246,0.38)";

export const TIER4_PHASE_LABELS = {
  "4.1": "MT5 Mastery",
  "4.2": "Building the SuPER EA",
  "4.3": "VPS + Webhook Pipeline",
  "4.4": "Optimization + Prop Firm",
  "4.5": "Algo Trader Graduation",
};

export function Tier4Header({ passed, total, unlocked, certified }) {
  return (
    <div data-testid="tier4-header" style={{
      display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap",
      marginTop: 36, marginBottom: 14, paddingTop: 18,
      borderTop: `1px solid ${THEME.border}`,
    }}>
      <div style={{ fontSize: 11, letterSpacing: 2.5, color: TIER4_BLUE, fontFamily: "monospace" }}>
        {unlocked ? "TIER 4 · LESSONS" : "🔒 TIER 4"}
      </div>
      <div style={{ color: THEME.text, fontSize: 22, fontWeight: 800, fontFamily: "'Bebas Neue', 'Oswald', sans-serif", letterSpacing: 0.5 }}>
        ALGO TRADING — MT5 &amp; Expert Advisors
      </div>
      {unlocked && (
        <div data-testid="tier4-progress" style={{ marginLeft: "auto", color: THEME.mute, fontFamily: "monospace", fontSize: 12 }}>
          {passed} / {total} passed
        </div>
      )}
      {certified && (
        <div data-testid="tier4-certified-pill" style={{
          marginLeft: unlocked ? 0 : "auto",
          padding: "4px 10px", border: `1px solid ${TIER4_BLUE}`, borderRadius: 999,
          color: TIER4_BLUE, fontFamily: "monospace", fontSize: 10, letterSpacing: 1.5,
          background: `${TIER4_BLUE}15`,
          boxShadow: `0 0 14px ${TIER4_BLUE_GLOW}`,
        }}>
          ⚡ ALGO TRADER
        </div>
      )}
    </div>
  );
}

export function Tier4GatePlaceholder() {
  return (
    <div data-testid="tier4-locked-placeholder" style={{
      padding: 22, background: "#0a121f", border: `1px dashed ${TIER4_BLUE}55`,
      borderLeft: `3px solid ${TIER4_BLUE}`, borderRadius: 10, textAlign: "center",
    }}>
      <div style={{ fontSize: 36, marginBottom: 6 }}>🔒</div>
      <div style={{ color: TIER4_BLUE, fontFamily: "monospace", fontSize: 11, letterSpacing: 2, marginBottom: 8 }}>TIER 4 LOCKED</div>
      <div style={{ color: THEME.text, fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: "0 auto" }}>
        Earn AI TRADER badge to unlock Algo Trading.
      </div>
      <div style={{ color: THEME.mute, fontSize: 12, lineHeight: 1.6, maxWidth: 480, margin: "8px auto 0", fontStyle: "italic" }}>
        25 lessons · 5 modules · MT5, Expert Advisors, VPS, prop-firm pipeline · ends with the ALGO TRADER credential.
      </div>
    </div>
  );
}

export function Tier5PreviewLocked() {
  return (
    <div data-testid="tier5-preview-locked" style={{
      marginTop: 28, padding: 22, background: "#0d1218", border: `1px dashed ${THEME.border}`,
      borderRadius: 10, textAlign: "center", opacity: 0.85,
    }}>
      <div style={{ fontSize: 28, marginBottom: 6 }}>🔒</div>
      <div style={{ color: THEME.mute, fontFamily: "monospace", fontSize: 10, letterSpacing: 2, marginBottom: 6 }}>TIER 5 · COMING SOON</div>
      <div style={{ color: THEME.text, fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: "0 auto" }}>
        MASTERY — Multi-Asset, Commodities &amp; Portfolio
      </div>
      <div style={{ color: THEME.mute, fontSize: 11, lineHeight: 1.6, marginTop: 8, fontFamily: "monospace" }}>
        UNLOCKS WHEN TIER 5 LESSONS ARE RELEASED
      </div>
    </div>
  );
}

// 7-item milestone gate: 2 auto + 5 self-reported "30-day autonomous EA".
export function Tier4MilestoneGate({ tier4Lessons, completion, onConfirm, onClose, modalBg, modalCard, primaryBtn, ghostBtn }) {
  const allLessonsDone = tier4Lessons.every((l) => completion[l.id]?.passed);
  const allQuizzesPassedAt75 = tier4Lessons.every((l) => (completion[l.id]?.score ?? 0) >= TIER4_PASS_THRESHOLD);
  const [mt5Setup, setMt5Setup] = useState(false);
  const [firstEa, setFirstEa] = useState(false);
  const [vpsAlerts, setVpsAlerts] = useState(false);
  const [testerPF, setTesterPF] = useState(false);
  const [liveAudit30, setLiveAudit30] = useState(false);

  const items = [
    { id: "lessons", label: "25 Tier 4 lessons completed", done: allLessonsDone, manual: false },
    { id: "quizzes", label: "All module quizzes passed (75% min)", done: allQuizzesPassedAt75, manual: false },
    { id: "mt5", label: "MT5 installed and configured", done: mt5Setup, manual: true, set: setMt5Setup },
    { id: "ea-built", label: "First EA built and tested", done: firstEa, manual: true, set: setFirstEa },
    { id: "vps", label: "VPS running with Telegram alerts", done: vpsAlerts, manual: true, set: setVpsAlerts },
    { id: "tester-pf", label: "Strategy Tester report passed PF > 1.5", done: testerPF, manual: true, set: setTesterPF },
    { id: "live-30", label: "30-day live EA performance audit done", done: liveAudit30, manual: true, set: setLiveAudit30 },
  ];
  const allReady = items.every((it) => it.done);

  return (
    <Portal>
      <div data-testid="tier4-milestone-gate-modal" style={modalBg}>
        <div style={{ ...modalCard, borderColor: `${TIER4_BLUE}55`, boxShadow: `0 0 60px ${TIER4_BLUE_GLOW}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: "monospace", color: TIER4_BLUE, fontSize: 10, letterSpacing: 2 }}>TIER 4 · MILESTONE GATE</div>
              <h2 style={{ margin: "6px 0 0", color: THEME.text, fontSize: 24, fontFamily: "'Bebas Neue','Oswald',sans-serif", letterSpacing: 0.5 }}>
                YOU BUILT THE MACHINE. NOW PROVE IT WORKS.
              </h2>
            </div>
            <button data-testid="tier4-gate-close" onClick={onClose} style={ghostBtn}>Close</button>
          </div>

          <div style={{ display: "grid", gap: 8, marginTop: 6, marginBottom: 16 }}>
            {items.map((it) => (
              <label key={it.id} data-testid={`tier4-gate-item-${it.id}`}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: 12, borderRadius: 8,
                  background: it.done ? `${TIER4_BLUE}10` : "#0d1218",
                  border: `1px solid ${it.done ? TIER4_BLUE : THEME.border}`,
                  borderLeft: `3px solid ${TIER4_BLUE}`,
                  cursor: it.manual ? "pointer" : "default",
                }}>
                <input type="checkbox" checked={it.done}
                  disabled={!it.manual}
                  onChange={(e) => it.manual && it.set(e.target.checked)}
                  data-testid={`tier4-gate-check-${it.id}`}
                  style={{ width: 18, height: 18, accentColor: TIER4_BLUE, cursor: it.manual ? "pointer" : "not-allowed" }} />
                <span style={{ color: it.done ? THEME.text : THEME.mute, fontSize: 13, lineHeight: 1.5, flex: 1 }}>
                  {it.label}
                  {!it.manual && it.done && <span style={{ color: TIER4_BLUE, fontFamily: "monospace", fontSize: 10, marginLeft: 8 }}>· auto-verified</span>}
                </span>
              </label>
            ))}
          </div>

          <div style={{
            padding: 14, marginBottom: 16, borderRadius: 10,
            background: `${TIER4_BLUE}10`, border: `1px solid ${TIER4_BLUE}55`, borderLeft: `3px solid ${TIER4_BLUE}`,
            color: THEME.text, fontSize: 13, lineHeight: 1.7, fontStyle: "italic",
          }}>
            <div style={{ fontFamily: "monospace", color: TIER4_BLUE, fontSize: 9, letterSpacing: 2, marginBottom: 6, fontStyle: "normal" }}>AXEL — MILESTONE GATE</div>
            "You built the machine. Now prove it works. 30 days. Live or demo. EA running 24/7. Telegram alerts firing. Helix tracking. Come back when the machine has traded without you for a full month."
          </div>

          <button data-testid="tier4-gate-confirm-btn" onClick={onConfirm}
            disabled={!allReady}
            style={{
              ...primaryBtn, width: "100%", padding: "14px 18px", fontSize: 13,
              background: TIER4_BLUE, color: "#0a0a0a", borderColor: TIER4_BLUE,
              opacity: allReady ? 1 : 0.4, cursor: allReady ? "pointer" : "not-allowed",
            }}>
            MY EA HAS RUN 30 DAYS AUTONOMOUSLY →
          </button>
          {!allReady && (
            <div data-testid="tier4-gate-waiting" style={{ marginTop: 10, color: THEME.mute, fontSize: 11, fontFamily: "monospace", textAlign: "center", letterSpacing: 1 }}>
              Tick all 7 items above to confirm.
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}

// Opens a printable one-page EA Performance Attestation in a new window.
// Zero deps — uses the browser's native "Save as PDF" via window.print().
function openAttestationPrintView({ name, dateStr, xp, verifyCode, verifyUrl }) {
  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) return;
  const gateConfirmed = flexxState.tier4GateConfirmed() || {};
  const eaDays = gateConfirmed.eaAutonomousDays || 30;
  const attested = [
    "MT5 installed and configured",
    "First EA built and tested",
    "VPS running with Telegram alerts",
    "Strategy Tester report passed PF &gt; 1.5",
    `${eaDays}-day live EA performance audit completed`,
  ];
  const verifyBlock = verifyCode ? `
    <h2>Verification</h2>
    <div style="display:flex; justify-content:space-between; gap:16px; padding:10px 14px; border:1px solid ${TIER4_BLUE}; background:${TIER4_BLUE}10; border-radius:6px; font-family:'Courier New',monospace; font-size:11px; align-items:center;">
      <div>
        <div style="color:#666; letter-spacing:2px; font-size:9px; margin-bottom:2px;">CERTIFICATE ID</div>
        <div style="color:${TIER4_BLUE}; font-weight:700; letter-spacing:1px;">${verifyCode}</div>
      </div>
      <div style="text-align:right;">
        <div style="color:#666; letter-spacing:2px; font-size:9px; margin-bottom:2px;">VERIFY AT</div>
        <div style="color:#222;">${verifyUrl || `/verify/${verifyCode}`}</div>
      </div>
    </div>
    <div style="margin-top:8px; font-size:10px; color:#888; font-style:italic;">
      Any third party may confirm this credential at the URL above. The server
      cross-checks an HMAC-SHA256 signature bound to this certificate ID.
    </div>
  ` : `
    <div style="margin-top:14px; padding:10px 14px; border:1px dashed #bbb; border-radius:6px; font-family:'Courier New',monospace; font-size:10px; color:#888;">
      Offline issuance — certificate not backend-signed. Complete gate while online
      to receive a verifiable certificate ID.
    </div>
  `;
  const html = `<!doctype html>
<html><head><meta charset="utf-8" /><title>EA Performance Attestation — ${name}</title>
<style>
  @page { size: A4; margin: 18mm; }
  * { box-sizing: border-box; }
  body { font-family: Georgia, "Times New Roman", serif; color: #0a0a0a; margin: 0; padding: 32px; background: #fff; }
  .wrap { max-width: 720px; margin: 0 auto; border: 2px solid ${TIER4_BLUE}; padding: 36px 40px; position: relative; }
  .wrap::before { content: ""; position: absolute; inset: 6px; border: 1px solid ${TIER4_BLUE}55; pointer-events: none; }
  .eyebrow { font-family: "Courier New", monospace; letter-spacing: 3px; color: ${TIER4_BLUE}; font-size: 11px; text-transform: uppercase; margin-bottom: 6px; }
  h1 { font-family: "Helvetica Neue", Arial, sans-serif; font-size: 34px; margin: 0 0 4px; letter-spacing: 1px; text-transform: uppercase; }
  .sub { font-family: "Courier New", monospace; color: #555; letter-spacing: 2px; font-size: 11px; margin-bottom: 22px; }
  .divider { height: 1px; background: linear-gradient(90deg, transparent, ${TIER4_BLUE}, transparent); margin: 18px 0 22px; }
  .intro { font-size: 13px; color: #555; margin-bottom: 6px; }
  .name { font-size: 26px; font-weight: 700; letter-spacing: 0.5px; margin: 4px 0 18px; border-bottom: 1px dashed #ccc; padding-bottom: 10px; }
  .body { font-size: 13px; line-height: 1.7; color: #222; margin-bottom: 22px; }
  h2 { font-size: 12px; letter-spacing: 2.5px; color: ${TIER4_BLUE}; font-family: "Courier New", monospace; text-transform: uppercase; margin: 22px 0 10px; }
  ul { padding-left: 18px; margin: 0 0 20px; }
  li { font-size: 13px; line-height: 1.8; color: #222; }
  .badge { display: inline-block; padding: 8px 16px; border: 1.5px solid ${TIER4_BLUE}; border-radius: 999px; color: ${TIER4_BLUE}; font-family: "Courier New", monospace; font-size: 11px; letter-spacing: 1.5px; }
  .meta { display: flex; justify-content: space-between; margin-top: 24px; padding-top: 14px; border-top: 1px solid #ddd; font-family: "Courier New", monospace; font-size: 10px; color: #666; letter-spacing: 1px; }
  .sig { margin-top: 18px; display: flex; justify-content: space-between; font-family: "Courier New", monospace; font-size: 10px; color: #666; letter-spacing: 1px; }
  .disclaimer { margin-top: 18px; font-size: 10px; color: #888; font-style: italic; line-height: 1.5; }
  @media print { body { padding: 0; } .noprint { display: none !important; } }
  .noprint { position: fixed; top: 12px; right: 12px; display: flex; gap: 8px; }
  .noprint button { padding: 8px 14px; font-size: 12px; border: 1px solid ${TIER4_BLUE}; background: ${TIER4_BLUE}; color: #fff; border-radius: 4px; cursor: pointer; font-family: inherit; }
  .noprint button.secondary { background: #fff; color: ${TIER4_BLUE}; }
</style></head>
<body>
  <div class="noprint">
    <button onclick="window.print()">Save / Print as PDF</button>
    <button class="secondary" onclick="window.close()">Close</button>
  </div>
  <div class="wrap">
    <div class="eyebrow">FLEXX Academy · Algo Trading</div>
    <h1>EA Performance Attestation</h1>
    <div class="sub">Tier 4 — Algo Trader Credential</div>
    <div class="divider"></div>
    <div class="intro">This document attests that</div>
    <div class="name">${name}</div>
    <div class="body">
      has completed all 25 Tier 4 lessons of the FLEXX Academy Algo Trading
      curriculum, built and deployed the SuPER Expert Advisor, and operated a
      live autonomous algorithmic system on MT5 for a continuous ${eaDays}-day
      period. The holder has self-attested to the operational milestones below.
    </div>
    <h2>Attested Operational Milestones</h2>
    <ul>
      ${attested.map((it) => `<li>${it}</li>`).join("")}
    </ul>
    <div class="badge">⚡ ALGO TRADER — Tier 4 Certified</div>
    <div class="meta">
      <span>ISSUED · ${dateStr}</span>
      <span>+${xp} XP</span>
    </div>
    ${verifyBlock}
    <div class="sig">
      <span>Signature — Candidate</span>
      <span>Signature — FLEXX Academy</span>
    </div>
    <div class="disclaimer">
      This attestation reflects the candidate's self-reported completion of the
      Tier 4 curriculum and 30-day autonomous EA operation. FLEXX Academy does
      not verify individual trading results. For prop-firm application use only;
      not a financial performance guarantee.
    </div>
  </div>
  <script>setTimeout(function(){ try { window.focus(); } catch(e){} }, 120);</script>
</body></html>`;
  w.document.open();
  w.document.write(html);
  w.document.close();
}

// Electric-blue ALGO TRADER certificate.
export function Tier4CertificateModal({ onClose, modalBg, modalCard, primaryBtn, ConfettiComp }) {
  const profile = flexxState.profile() || {};
  const issued = flexxState.tier4Graduate();
  const dateStr = issued
    ? new Date(issued).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : "—";
  const studentName = profile.name || "FLEXX Trader";
  const signedCert = (flexxState.tierCerts() || {})["4"] || null;
  const verifyCode = signedCert?.cert_id || null;
  const verifyUrl = signedCert?.verify_url || (verifyCode ? `${window.location.origin}/verify/${verifyCode}` : null);
  const onDownloadCert = () => openCertificatePrintView({
    studentName, tier: "algo",
    courseName: "Algorithmic Trading — MT5 & Expert Advisors",
    courseSubtitle: "The capstone of the FLEXX ACADEMY Elite Trading Series",
    completionDate: dateStr, verifyCode: verifyCode || "",
  });
  const onDownloadAttestation = () => openAttestationPrintView({
    name: studentName, dateStr, xp: TIER4_TIER_BONUS, verifyCode, verifyUrl,
  });
  return (
    <Portal>
      <div data-testid="tier4-certificate-modal">
        {ConfettiComp && <ConfettiComp />}
        <FlexxCertificate
          studentName={studentName}
          courseName="Algorithmic Trading — MT5 & Expert Advisors"
          courseSubtitle="The capstone of the FLEXX ACADEMY Elite Trading Series"
          completionDate={dateStr}
          verifyCode={verifyCode || ""}
          tier="algo"
          onClose={onClose}
          onDownload={onDownloadCert} />
        {/* Secondary action — prop-firm attestation PDF — rendered into the
            same fixed-bottom toolbar area as the certificate's own buttons. */}
        <div style={{ position: "fixed", bottom: 24, left: 0, right: 0,
          display: "flex", justifyContent: "center", pointerEvents: "none", zIndex: 9810 }}>
          <div style={{ pointerEvents: "auto", display: "flex", gap: 10, alignItems: "center",
            background: "rgba(0,0,0,0.8)", padding: "8px 12px", borderRadius: 8,
            border: `1px solid ${TIER4_BLUE}55`, transform: "translateY(54px)" }}>
            <button data-testid="tier4-attestation-download-btn" onClick={onDownloadAttestation}
              style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, letterSpacing: 2,
                fontSize: 11, padding: "8px 16px", cursor: "pointer",
                background: "transparent", color: TIER4_BLUE,
                border: `1px solid ${TIER4_BLUE}`, borderRadius: 4 }}>
              ⬇ EA ATTESTATION (PROP-FIRM)
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
